'use client';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { EPlatform } from '@/utils/enums/platform-enum';
import { setSession } from '@/utils/tools';
import { cva } from 'class-variance-authority';
import Image from '@/components/Image';
import Link from 'next/link';
import { useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import Banner from './components/Banner';
import ProductList from './components/ProductList';
import { useDenimGlassesTrack } from './hooks/useDenimGlassesTrack';
import type { DenimGlassesClientProps, DenimPageData } from './types';

export default function DenimGlassesClient({ initialData }: DenimGlassesClientProps) {
  const { deviceType } = useContext(ResponsiveContext);

  const { navigate } = useAppNavigation();
  const { sdDenimGlassesPageMktClick, sdDenimGlassesPageMktExp } = useDenimGlassesTrack();

  const pageData: DenimPageData = initialData;

  // 底部区域曝光检测
  const [bottomRef, bottomInView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 处理底部区域曝光
  useEffect(() => {
    if (bottomInView && pageData.bottom_part.bg_img) {
      sdDenimGlassesPageMktExp({
        mkt_name: `Banner_3`,
        shop_sku: ``,
        business_sku: ``,
        product_sku: ``,
        group_id: `${pageData.bottom_part.jump_id}`,
      });
    }
  }, [bottomInView, pageData.bottom_part, sdDenimGlassesPageMktExp]);

  // 处理底部Banner点击
  const handleBottomBannerClick = () => {
    sdDenimGlassesPageMktClick(`Banner_3`, {
      shop_sku: ``,
      business_sku: ``,
      product_sku: ``,
      group_id: `${pageData.bottom_part.jump_id}`,
    });
    setSession('zl_goodsListEnterPageScene', '活动专题页');
    navigate(`/goods-list/${pageData.bottom_part.jump_id}`);
  };

  const isWeb = deviceType === EPlatform.WEB;

  // cva: 底部文案容器样式（PC/M 区分 padding）
  const bottomPartCva = cva('bottom-part text-center relative bg-cover bg-no-repeat bg-center', {
    variants: {
      variant: {
        wap: 'p-[27px_0_21px]',
        web: 'p-[3.65vw_0_2.97vw]',
      },
    },
    defaultVariants: { variant: 'wap' },
  });

  // cva: 底部标题右侧小图标定位
  const bottomTitleImgCva = cva('absolute h-auto', {
    variants: {
      variant: {
        wap: 'right-[-22px] top-0 w-[34px]',
        web: 'right-[-3.125vw] top-[0.52vw] w-[4.17vw]',
      },
    },
    defaultVariants: { variant: 'wap' },
  });

  // cva: 底部标题文本样式
  const bottomTitleTextCva = cva('inline-block font-bb text-[#0e51ca]  border-[#0e51ca]', {
    variants: {
      variant: {
        wap: 'text-[28px] border-b-[2.5px]',
        web: 'text-[3.85vw] border-b-[4px] leading-[4.2vw]',
      },
    },
    defaultVariants: { variant: 'wap' },
  });

  // cva: 底部副标题文本样式
  const bottomSubheadingCva = cva(' text-[#0e51ca] font-normal', {
    variants: {
      variant: {
        wap: 'text-[28px] mt-3',
        web: 'text-[3.85vw] pl-[2.08vw]',
      },
    },
    defaultVariants: { variant: 'wap' },
  });

  // cva: 底部描述文本样式
  const bottomDescriptionCva = cva('word  text-black', {
    variants: {
      variant: {
        wap: 'px-[30px] pt-[2px] text-[12px] leading-[18px]',
        web: 'px-[8.85vw] pt-[2.34vw] text-[max(1.14vw,14px)] leading-[1.46vw]',
      },
    },
    defaultVariants: { variant: 'wap' },
  });

  // cva: 底部标题容器样式
  const bottomTitleCva = cva('shop-box  relative w-fit no-underline cursor-pointer block ', {
    variants: {
      variant: {
        wap: 'text-[28px] border-b-[2.5px] border-[#0e51ca]',
        web: 'text-[3.85vw]',
      },
    },
    defaultVariants: { variant: 'wap' },
  });

  return (
    <div className="denim-glasses-page w-full bg-white min-h-screen">
      {/* Banner 区域 */}
      <Banner
        banner1={pageData.banner1}
        banner2={pageData.banner2}
        banner3={pageData.banner3}
        isWeb={isWeb}
        onMktClick={sdDenimGlassesPageMktClick}
        onMktExp={sdDenimGlassesPageMktExp}
      />

      {/* 商品列表 */}
      <ProductList
        productLists={pageData.product_lists}
        hotImg={pageData.hot_img}
        isWeb={isWeb}
        onMktExp={sdDenimGlassesPageMktExp}
        onMktClick={sdDenimGlassesPageMktClick}
      />

      {/* 底部文案区域（纯 HTML + Tailwind，按 Vue 样式实现） */}
      {pageData.bottom_part.bg_img && (
        <div
          ref={bottomRef as any}
          className={bottomPartCva({ variant: isWeb ? 'web' : 'wap' })}
          style={{ backgroundImage: `url(${pageData.bottom_part.bg_img})` }}
        >
          <div className={`title flex items-center justify-center ${isWeb ? '' : 'flex-col'}`}>
            <Link
              href={`/goods-list/${pageData.bottom_part.jump_id}`}
              className={bottomTitleCva({ variant: isWeb ? 'web' : 'wap' })}
              onClick={(e) => {
                e.preventDefault();
                handleBottomBannerClick();
              }}
            >
              <span className={bottomTitleTextCva({ variant: isWeb ? 'web' : 'wap' })}>
                {pageData.bottom_part.main_heading}
              </span>
              {pageData.bottom_part.title_img && (
                <Image
                  src={pageData.bottom_part.title_img}
                  alt="Shop All Icon"
                  width={34}
                  height={34}
                  className={bottomTitleImgCva({ variant: isWeb ? 'web' : 'wap' })}
                />
              )}
            </Link>

            <h1 className={bottomSubheadingCva({ variant: isWeb ? 'web' : 'wap' })}>
              {pageData.bottom_part.subheading}
            </h1>
          </div>

          <p
            className={bottomDescriptionCva({ variant: isWeb ? 'web' : 'wap' })}
            dangerouslySetInnerHTML={{ __html: pageData.bottom_part.description }}
          />
        </div>
      )}
    </div>
  );
}
